import type { ChangeEvent } from "react";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { KbAdminCoverageStrip } from "./KbAdminCoverageStrip";
import { SectionCard } from "./SectionCard";
export type KbAdminWorkspaceChromeProps = {
  kbMode: "nav" | "library" | "editor" | "import" | "gap_inbox" | "trash" | "proposals";
  setKbMode: (mode: KbAdminWorkspaceChromeProps["kbMode"]) => void;
  gapInboxOpenCount: number;
  pendingProposalCount: number;
  refreshGapInbox: () => void | Promise<void>;
  canPublishKb: boolean;
  editorName: string;
  draftCount: number;
  loadedArticle: { version?: number | string } | null;
  startNewArticle: () => void;
  clearWorkspace: () => void;
  onBulkImportFilePicked: (event: ChangeEvent<HTMLInputElement>) => void;
  hasLegacyAdminToken: boolean;
  onLogout?: () => void;
  logoutLegacyAdmin: () => void;
  aiGuidance: string[];
  onOpenTrash: () => void | Promise<void>;
};

export function KbAdminWorkspaceChrome(props: KbAdminWorkspaceChromeProps) {
  const {
    kbMode,
    setKbMode,
    gapInboxOpenCount,
    pendingProposalCount,
    refreshGapInbox,
    canPublishKb,
    editorName,
    draftCount,
    loadedArticle,
    startNewArticle,
    clearWorkspace,
    onBulkImportFilePicked,
    hasLegacyAdminToken,
    onLogout,
    logoutLegacyAdmin,
    aiGuidance,
    onOpenTrash,
  } = props;

  if (kbMode === "nav") {
    return (
      <div {...{ [FIREFLY_TARGET_ATTR]: "kb-workspace" }}>
      <SectionCard
        title="Knowledge Base workspace"
        description="Search the library, draft articles with AI assistance, review knowledge gaps, and batch import documentation."
        rightSlot={
          <div className="flex items-center gap-2">
            {gapInboxOpenCount > 0 ? (
              <button
                type="button"
                className="text-[11px] rounded-full border border-amber-400 bg-amber-100 px-2 py-1 text-amber-900 font-semibold animate-pulse dark:border-amber-600 dark:bg-amber-950/50 dark:text-amber-100"
                onClick={() => {
                  void refreshGapInbox();
                  setKbMode("gap_inbox");
                }}
              >
                {gapInboxOpenCount} gap{gapInboxOpenCount === 1 ? "" : "s"}
              </button>
            ) : null}
            <span className="text-[11px] rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 text-indigo-700 dark:border-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-200">
              {canPublishKb ? "publish access" : "draft access"}
            </span>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="space-y-1">
            <div className="text-xs text-gray-600 dark:text-zinc-300">
              Signed in as <span className="font-medium text-gray-900 dark:text-zinc-100">{editorName || "complete profile in Settings"}</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-zinc-400">
              {canPublishKb
                ? "You can create drafts, update existing content, and publish changes."
                : "Trainer-level access can search, review, and save draft proposals. Published articles stay locked until an admin publishes changes."}
            </div>
            <p className="text-[11px] text-gray-500 dark:text-zinc-400 pt-1">
              Almost all editing here applies to the <strong className="font-medium text-gray-700 dark:text-gray-300">selected organization</strong>{" "}
              (org KB), alongside optional app- or manufacturer-visible articles your role can see.
            </p>
          </div>

          <KbAdminCoverageStrip />

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <button
              type="button"
              {...{ [FIREFLY_TARGET_ATTR]: "kb-library-tile" }}
              className="flex flex-col items-start rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-left hover:border-indigo-300 hover:bg-indigo-100 transition dark:border-indigo-800 dark:bg-indigo-950/40 dark:hover:border-indigo-600 dark:hover:bg-indigo-900/50"
              onClick={() => setKbMode("library")}
            >
              <div className="text-sm font-semibold text-indigo-900 dark:text-indigo-100">Library</div>
              <div className="mt-1 text-xs text-indigo-700 dark:text-indigo-200">Search and browse articles</div>
              {draftCount > 0 ? <div className="mt-2 text-[11px] text-indigo-600 dark:text-indigo-300">{draftCount} draft{draftCount === 1 ? "" : "s"}</div> : null}
            </button>
            <button
              type="button"
              {...{ [FIREFLY_TARGET_ATTR]: "kb-editor-tile" }}
              className="flex flex-col items-start rounded-2xl border border-gray-200 bg-gray-50 p-4 text-left hover:border-gray-300 hover:bg-white transition dark:border-zinc-700 dark:bg-zinc-900/40 dark:hover:border-zinc-600 dark:hover:bg-zinc-800/60"
              onClick={() => setKbMode("editor")}
            >
              <div className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Editor</div>
              <div className="mt-1 text-xs text-gray-600 dark:text-zinc-300">Write and save articles with AI assistance</div>
              {loadedArticle ? <div className="mt-2 text-[11px] text-gray-500 dark:text-zinc-400">v{loadedArticle.version ?? "?"} loaded</div> : null}
            </button>
            <button
              type="button"
              {...{ [FIREFLY_TARGET_ATTR]: "kb-gap-inbox-tile" }}
              className={
                "flex flex-col items-start rounded-2xl border p-4 text-left transition " +
                (gapInboxOpenCount > 0
                  ? "border-amber-300 bg-amber-50 hover:border-amber-400 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/35 dark:hover:border-amber-600 dark:hover:bg-amber-900/45"
                  : "border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-white dark:border-zinc-700 dark:bg-zinc-900/40 dark:hover:border-zinc-600 dark:hover:bg-zinc-800/60")
              }
              onClick={() => {
                void refreshGapInbox();
                setKbMode("gap_inbox");
              }}
            >
              <div className={`text-sm font-semibold ${gapInboxOpenCount > 0 ? "text-amber-900 dark:text-amber-100" : "text-gray-900 dark:text-zinc-100"}`}>Gap Inbox</div>
              <div className={`mt-1 text-xs ${gapInboxOpenCount > 0 ? "text-amber-800 dark:text-amber-200" : "text-gray-600 dark:text-zinc-300"}`}>
                {gapInboxOpenCount > 0 ? `${gapInboxOpenCount} open item${gapInboxOpenCount === 1 ? "" : "s"}` : "Review knowledge gaps"}
              </div>
            </button>
            <button
              type="button"
              className="flex flex-col items-start rounded-2xl border border-gray-200 bg-gray-50 p-4 text-left hover:border-gray-300 hover:bg-white transition dark:border-zinc-700 dark:bg-zinc-900/40 dark:hover:border-zinc-600 dark:hover:bg-zinc-800/60"
              onClick={() => setKbMode("import")}
            >
              <div className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Bulk Import</div>
              <div className="mt-1 text-xs text-gray-600 dark:text-zinc-300">Batch-create from JSON</div>
            </button>
            {canPublishKb ? (
              <button
                type="button"
                className={
                  "flex flex-col items-start rounded-2xl border p-4 text-left transition " +
                  (pendingProposalCount > 0
                    ? "border-violet-300 bg-violet-50 hover:border-violet-400 hover:bg-violet-100 dark:border-violet-700 dark:bg-violet-950/35 dark:hover:border-violet-600 dark:hover:bg-violet-900/45"
                    : "border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-white dark:border-zinc-700 dark:bg-zinc-900/40 dark:hover:border-zinc-600 dark:hover:bg-zinc-800/60")
                }
                onClick={() => setKbMode("proposals")}
              >
                <div className={`text-sm font-semibold ${pendingProposalCount > 0 ? "text-violet-900 dark:text-violet-100" : "text-gray-900 dark:text-zinc-100"}`}>
                  Change proposals
                </div>
                <div className={`mt-1 text-xs ${pendingProposalCount > 0 ? "text-violet-800 dark:text-violet-200" : "text-gray-600 dark:text-zinc-300"}`}>
                  {pendingProposalCount > 0
                    ? `${pendingProposalCount} pending`
                    : "Approve trainer edits to live articles"}
                </div>
              </button>
            ) : null}
            {canPublishKb ? (
              <button
                type="button"
                className="flex flex-col items-start rounded-2xl border border-red-200 bg-red-50 p-4 text-left hover:border-red-300 hover:bg-red-100 transition dark:border-red-800 dark:bg-red-950/35 dark:hover:border-red-700 dark:hover:bg-red-950/50"
                onClick={() => {
                  void onOpenTrash();
                  setKbMode("trash");
                }}
              >
                <div className="text-sm font-semibold text-red-900 dark:text-red-200">Trash</div>
                <div className="mt-1 text-xs text-red-700 dark:text-red-300">Permanently remove deleted articles</div>
              </button>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              {...{ [FIREFLY_TARGET_ATTR]: "kb-new-article" }}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700"
              onClick={() => {
                startNewArticle();
                setKbMode("editor");
              }}
            >
              New article
            </button>
            <button
              type="button"
              className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/45 dark:text-amber-100 dark:hover:bg-amber-900/55"
              onClick={() => {
                clearWorkspace();
                setKbMode("editor");
              }}
            >
              Clear workspace
            </button>
            <label className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm cursor-pointer text-gray-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700">
              Load import file
              <input type="file" accept="application/json,.json" className="hidden" onChange={onBulkImportFilePicked} />
            </label>
            {hasLegacyAdminToken && onLogout ? (
              <button
                type="button"
                className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-600 dark:bg-amber-950/45 dark:text-amber-100 dark:hover:bg-amber-900/50"
                onClick={logoutLegacyAdmin}
              >
                Logout legacy admin
              </button>
            ) : null}
          </div>

          <details className="rounded-xl border border-gray-100 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900/40">
            <summary className="cursor-pointer list-none px-3 py-2 text-xs font-medium text-gray-600 select-none dark:text-zinc-300">? Help & guidance</summary>
            <div className="border-t border-gray-100 px-3 py-3 space-y-1 text-xs text-gray-700 dark:border-zinc-700 dark:text-zinc-300">
              {aiGuidance.map((item) => (
                <div key={item}>{item}</div>
              ))}
              <div className="pt-2 text-gray-500 dark:text-zinc-400">
                Use Library to find and load articles into the Editor. Use Gap Inbox to review AI-identified knowledge gaps and query misses. Use Bulk Import to create multiple articles from a JSON file.
              </div>
            </div>
          </details>
        </div>
      </SectionCard>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-zinc-700 dark:bg-zinc-900/50">
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
          onClick={() => setKbMode("nav")}
        >
          ← Workspace
        </button>
        <span className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
          {kbMode === "library"
            ? "Library"
            : kbMode === "editor"
              ? "Editor"
              : kbMode === "gap_inbox"
                ? "Gap Inbox"
                : kbMode === "trash"
                  ? "Trash"
                  : kbMode === "proposals"
                    ? "Change proposals"
                  : "Bulk Import"}
        </span>
        {kbMode === "editor" && loadedArticle ? <span className="text-xs text-gray-500 dark:text-zinc-400">v{loadedArticle.version ?? "?"}</span> : null}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {kbMode === "editor" ? (
          <button
            type="button"
            className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-950 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/45 dark:text-amber-100 dark:hover:bg-amber-900/55"
            onClick={clearWorkspace}
          >
            Clear workspace
          </button>
        ) : null}
        {gapInboxOpenCount > 0 ? (
          <button
            type="button"
            className="text-[11px] rounded-full border border-amber-400 bg-amber-100 px-2 py-1 text-amber-900 font-semibold animate-pulse dark:border-amber-600 dark:bg-amber-950/50 dark:text-amber-100"
            onClick={() => {
              void refreshGapInbox();
              setKbMode("gap_inbox");
            }}
          >
            {gapInboxOpenCount} gap{gapInboxOpenCount === 1 ? "" : "s"}
          </button>
        ) : null}
        <span className="text-[11px] rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 text-indigo-700 dark:border-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-200">
          {canPublishKb ? "publish access" : "draft access"}
        </span>
      </div>
    </div>
  );
}
